import React, { useRef, useState, useEffect, useCallback } from "react";
import { Search, ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/utils/utils";
import { Close } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { IconButton } from "@/components/ui/icon-button";
import { isEmpty } from "lodash";

interface SearchNavigationProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  inputClassName?: string;
  minSearchLength?: number;
  searchScope?: string;
}

const SearchNavigation: React.FC<SearchNavigationProps> = ({
  placeholder,
  value = "",
  onChange,
  className,
  inputClassName,
  minSearchLength = 2,
  searchScope = "[data-searchable]" // Only search in elements with data-searchable attribute
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [matches, setMatches] = useState<HTMLElement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const markClassName = "search-highlight-mark";

  // Find text nodes ONLY within specified scope
  const findTextNodes = useCallback((): Text[] => {
    const nodes: Text[] = [];
    const searchableElements = document.querySelectorAll(searchScope);

    searchableElements.forEach((searchableElement) => {
      const walker = document.createTreeWalker(
        searchableElement,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            if (!node.textContent?.trim()) return NodeFilter.FILTER_REJECT;

            const parent = node.parentElement;
            if (!parent) return NodeFilter.FILTER_REJECT;

            // Skip script, style, and existing marks
            if (
              parent.tagName === "SCRIPT" ||
              parent.tagName === "STYLE" ||
              parent.tagName === "NOSCRIPT" ||
              parent.classList.contains(markClassName)
            ) {
              return NodeFilter.FILTER_REJECT;
            }

            // Skip hidden elements
            const style = window.getComputedStyle(parent);
            if (
              style.display === "none" ||
              style.visibility === "hidden" ||
              style.opacity === "0"
            ) {
              return NodeFilter.FILTER_REJECT;
            }

            return NodeFilter.FILTER_ACCEPT;
          }
        }
      );

      let node: Node | null;
      while ((node = walker.nextNode())) {
        nodes.push(node as Text);
      }
    });

    return nodes;
  }, [searchScope]);

  const createMark = (
    content: string,
    isHighlighted: boolean = false
  ): HTMLElement => {
    const mark = document.createElement("mark");
    mark.textContent = content;
    mark.className = cn(
      markClassName,
      "bg-surface-search-match text-inherit px-0 py-0 m-0 border-0",
      isHighlighted && "bg-surface-search-match-active"
    );
    return mark;
  };

  // Highlight matches in a text node
  const highlightMatches = (
    textNode: Text,
    searchValue: string
  ): HTMLElement[] => {
    const text = textNode.textContent || "";
    const searchLower = searchValue.toLowerCase();
    const textLower = text.toLowerCase();
    const matches: HTMLElement[] = [];
    const fragment = document.createDocumentFragment();
    let currentIndex = 0;

    while (currentIndex < text.length) {
      const foundIndex = textLower.indexOf(searchLower, currentIndex);
      if (foundIndex === -1) {
        // Add remaining text
        if (currentIndex < text.length) {
          fragment.appendChild(
            document.createTextNode(text.slice(currentIndex))
          );
        }
        break;
      }

      // Add text before match
      if (foundIndex > currentIndex) {
        fragment.appendChild(
          document.createTextNode(text.slice(currentIndex, foundIndex))
        );
      }

      // Create mark for match - preserve exact case from original text
      const exactMatch = text.slice(
        foundIndex,
        foundIndex + searchValue.length
      );
      const mark = createMark(exactMatch);
      fragment.appendChild(mark);
      matches.push(mark);

      currentIndex = foundIndex + searchValue.length;
    }

    if (matches.length > 0) {
      textNode.parentNode?.replaceChild(fragment, textNode);
    }

    return matches;
  };

  // Clear all highlights ONLY within search scope
  const clearHighlights = useCallback(() => {
    const searchableElements = document.querySelectorAll(searchScope);

    searchableElements.forEach((searchableElement) => {
      const marks = searchableElement.querySelectorAll(`.${markClassName}`);
      marks.forEach((mark) => {
        if (mark.parentNode) {
          mark.parentNode.replaceChild(
            document.createTextNode(mark.textContent || ""),
            mark
          );
        }
      });
    });

    // Normalize text nodes within searchable elements
    searchableElements.forEach((element) => {
      element.normalize();
    });
  }, [searchScope]);

  // Update matches when search value changes
  useEffect(() => {
    clearHighlights();
    setCurrentIndex(-1);

    if (!value?.trim() || value.trim().length < minSearchLength) {
      setMatches([]);
      return;
    }

    const textNodes = findTextNodes();
    const newMatches: HTMLElement[] = [];

    textNodes.forEach((node) => {
      const nodeMatches = highlightMatches(node, value);
      newMatches.push(...nodeMatches);
    });

    setMatches(newMatches);
    if (newMatches.length > 0) {
      setCurrentIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- highlightMatches is redefined each render; listing it would re-run the search on every render
  }, [value, minSearchLength, clearHighlights, findTextNodes]);

  // Update current match highlight
  useEffect(() => {
    matches.forEach((mark, index) => {
      if (index === currentIndex) {
        mark.classList.add("bg-surface-search-match-active");
        mark.classList.remove("bg-surface-search-match");

        // Scroll into view with offset for fixed header
        const rect = mark.getBoundingClientRect();
        const offset = 100;
        const absoluteTop = window.pageYOffset + rect.top - offset;

        window.scrollTo({
          top: absoluteTop,
          behavior: "smooth"
        });
      } else {
        mark.classList.remove("bg-surface-search-match-active");
        mark.classList.add("bg-surface-search-match");
      }
    });
  }, [currentIndex, matches]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearHighlights();
    };
  }, [clearHighlights]);

  // Navigation handlers
  // eslint-disable-next-line react-hooks/exhaustive-deps -- not memoized; memoizing would change when the key listener effect runs
  const handleNext = () => {
    if (matches.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % matches.length);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- not memoized; memoizing would change when the key listener effect runs
  const handlePrevious = () => {
    if (matches.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + matches.length) % matches.length);
  };

  const handleClearInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
      onChange?.("");
      inputRef.current.focus();
    }
  };

  // Keyboard handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (e.shiftKey) {
          handlePrevious();
        } else {
          handleNext();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNext, handlePrevious]);

  // Check if search is active
  const isSearchActive =
    value?.trim() && value.trim().length >= minSearchLength;

  const [isFocused, setIsFocused] = React.useState(false);

  return (
    <div
      className={cn(
        "search-navigation",
        "flex items-center gap-2 border-[0.5px] rounded-lg bg-surface-field-default min-h-9 max-h-9",
        isFocused
          ? "border-[1px] border-primary bg-surface-focus-selected shadow-field-focus"
          : "border-border-field-default hover:border-border-field-hover shadow-field",
        "px-2 py-[6px]",
        className
      )}
      data-test="search-navigation"
    >
      <div className="flex items-center pointer-events-none shrink-0">
        <Search size={20} className="text-tertiary" />
      </div>
      <input
        ref={inputRef}
        type="text"
        className={cn(
          "w-full min-w-0 text-base font-normal leading-[18px] text-primary placeholder:text-tertiary bg-transparent outline-none",
          inputClassName
        )}
        placeholder={placeholder || "Search"}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        data-test="search-navigation-input"
      />
      <div className="flex items-center gap-1">
        <Text variant="label" className="text-sm">
          {matches.length > 0 ? `(${currentIndex + 1}/${matches.length})` : ""}
        </Text>
        <div className="flex">
          <IconButton
            variant="iconNoBg"
            size="sm"
            iconColor="secondary"
            onClick={handlePrevious}
            disabled={!isSearchActive || matches.length === 0}
            className={cn(
              "!size-5",
              (!isSearchActive || matches.length === 0) && isEmpty(value)
                ? "invisible"
                : "visible"
            )}
          >
            <ChevronUp size={20} />
          </IconButton>
          <IconButton
            variant="iconNoBg"
            size="sm"
            iconColor="secondary"
            onClick={handleNext}
            disabled={!isSearchActive || matches.length === 0}
            className={cn(
              "!size-5",
              (!isSearchActive || matches.length === 0) && isEmpty(value)
                ? "invisible"
                : "visible"
            )}
          >
            <ChevronDown size={20} />
          </IconButton>
        </div>
        <IconButton
          variant="iconNoBg"
          iconColor="secondary"
          size="sm"
          onClick={handleClearInput}
          className={cn(
            "shrink-0 h-full flex items-center justify-center w-[24px]",
            !isEmpty(value) ? "visible" : "invisible"
          )}
        >
          <Close />
        </IconButton>
      </div>
    </div>
  );
};

export default SearchNavigation;
